import dayjs from "dayjs";
import * as React from "react";

import { RobotoButton } from "@/shared/components";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  CollectionRecord,
  CollectionResourceType,
} from "@/shared/domain/collections";
import { useAddToCollections } from "@/shared/domain/collections/hooks/useAddToCollections";
import { useCollectionsQuery } from "@/shared/domain/collections/hooks/useCollectionsQuery";
import { Loader2, Plus, Search, X } from "@/shared/foundation/icons";
import { useDebouncedCallback } from "@/shared/hooks";
import { userFacingErrorMessage } from "@/shared/http";
import { pluralize } from "@/shared/text";

import styles from "./AddToCollection.module.css";
import { resourcesOf, resourceTypeOf } from "./addToCollectionHelpers";
import { SelectedResources } from "./selectedResources";

// ── local display helpers (only used within this file) ────────────────────────

/**
 * A `Record` over the union rather than a switch with a `default`, so a new
 * member type is a compile error here instead of being silently labelled as
 * the default arm's type. The `??` still covers a `resource_type` outside the
 * union, which arrives through an unchecked response cast.
 */
const SECTION_LABELS: Record<CollectionResourceType, string> = {
  dataset: "Dataset collections",
  event: "Event collections",
  file: "File collections",
  session: "Session collections",
};

function sectionLabel(resourceType: CollectionResourceType): string {
  return SECTION_LABELS[resourceType] ?? "Collections";
}

function initialsOf(name: string): string {
  return (
    name
      .replace(/[^a-z0-9 ]/gi, "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join("") || "•"
  );
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = ((h * 31 + s.charCodeAt(i)) | 0) >>> 0;
  }
  return h;
}

// Colors for each bucket live in CSS as `[data-bucket="N"]` rules
// (see AddToCollection.module.css) — this constant only needs the count.
const BUCKET_COUNT = 6;

function bucketFor(id: string): number {
  return hashString(id) % BUCKET_COUNT;
}

function formatRelativeTime(iso: string): string {
  const now = dayjs();
  const then = dayjs(iso);
  const diffSecs = now.diff(then, "second");
  const diffMins = now.diff(then, "minute");
  const diffHours = now.diff(then, "hour");
  const diffDays = now.diff(then, "day");

  if (diffSecs < 60) {
    return "just now";
  }
  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays === 1) {
    return "yesterday";
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  if (diffDays < 30) {
    return `${now.diff(then, "week")}w ago`;
  }
  if (diffDays < 365) {
    return `${now.diff(then, "month")}mo ago`;
  }
  return `${now.diff(then, "year")}y ago`;
}

interface SelectedStripProps {
  chosenList: CollectionRecord[];
  onRemove: (collection: CollectionRecord) => void;
}

function SelectedStrip({ chosenList, onRemove }: SelectedStripProps) {
  if (chosenList.length === 0) {
    return null;
  }
  return (
    <div className={styles.selectedStrip}>
      {chosenList.map((c) => {
        return (
          <span key={c.collection_id} className={styles.pill}>
            <span
              className={styles.pillSwatch}
              data-bucket={bucketFor(c.collection_id)}
            />
            {c.name}
            <button
              className={styles.pillRemove}
              onClick={() => onRemove(c)}
              aria-label={`Remove ${c.name ?? c.collection_id}`}
            >
              <X size={10} />
            </button>
          </span>
        );
      })}
    </div>
  );
}

interface CollectionRowProps {
  collection: CollectionRecord;
  checked: boolean;
  onToggle: () => void;
}

function CollectionRow({ collection, checked, onToggle }: CollectionRowProps) {
  const initials = initialsOf(collection.name ?? collection.collection_id);
  const updated = formatRelativeTime(collection.updated);

  return (
    <div
      className={styles.item}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
      role="option"
      aria-selected={checked}
      tabIndex={0}
    >
      <div
        className={styles.swatch}
        data-bucket={bucketFor(collection.collection_id)}
      >
        {initials}
      </div>
      <div className={styles.itemMeta}>
        <div className={styles.itemName}>
          {collection.name ?? collection.collection_id}
        </div>
        <div className={styles.itemInfo}>updated {updated}</div>
      </div>
      <div className={styles.itemIndicator}>
        <input
          type="checkbox"
          className={styles.checkbox}
          checked={checked}
          onChange={() => {}}
          tabIndex={-1}
          aria-hidden
        />
      </div>
    </div>
  );
}

interface CollectionsPickerProps {
  selected: SelectedResources;
  onCancel: () => void;
  onSuccess?: () => void;
  /**
   * Called with `true` as an add starts and `false` as it settles. The owner
   * uses it to refuse to dismiss the panel while the writes are in flight.
   */
  onPendingChange?: (pending: boolean) => void;
  onCreateNew: () => void;
  /**
   * Org whose collections are listed and written to. While undefined, no
   * collections are fetched and submitting does nothing.
   */
  orgId: string | undefined;
  /** Collections to leave out of the list. */
  excludeCollectionIds?: string[];
  /**
   * Owned by the parent `AddToCollection` component (not this one) so the
   * selection survives switching to the create-collection view and back —
   * this component unmounts on that switch, but the parent doesn't.
   */
  chosenRecords: Map<string, CollectionRecord>;
  setChosenRecords: React.Dispatch<
    React.SetStateAction<Map<string, CollectionRecord>>
  >;
}

export function CollectionsPicker({
  selected,
  onCancel,
  onSuccess,
  onPendingChange,
  onCreateNew,
  orgId,
  excludeCollectionIds,
  chosenRecords,
  setChosenRecords,
}: CollectionsPickerProps) {
  const [searchText, setSearchText] = React.useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState("");
  const [isSearchPending, setIsSearchPending] = React.useState(false);

  const debouncedSetSearchQuery = useDebouncedCallback((value: string) => {
    setDebouncedSearchQuery(value);
    setIsSearchPending(false);
  }, 250);

  const showNotification = useNotification();

  const resourceType = resourceTypeOf(selected);
  const resources = resourcesOf(selected);

  const {
    data: collectionsData,
    isFetching,
    error: fetchError,
  } = useCollectionsQuery({
    orgId,
    resourceType,
    nameFilter: debouncedSearchQuery || undefined,
    excludeIds: excludeCollectionIds,
    sortBy: "updated",
  });

  const collections = collectionsData?.items ?? [];

  // Set when some, but not all, of the chosen collections took the items.
  // Shown in the banner in place of a thrown error, which `useAddToCollections`
  // only raises when every write failed.
  const [partialFailure, setPartialFailure] =
    React.useState<React.ReactNode>(null);

  const {
    mutate: addToCollections,
    isPending,
    error: mutationError,
  } = useAddToCollections();

  // The writes cannot be cancelled once sent, so the owner must not let the
  // panel be dismissed while they are in flight. That also keeps this
  // component mounted for the callbacks below, which `mutate` drops once the
  // component that called it is gone.
  React.useEffect(() => {
    onPendingChange?.(isPending);
    // Reports `false` if this picker goes away mid-add (the user switched to
    // the create form), so the owner is never left refusing to close.
    return () => onPendingChange?.(false);
  }, [isPending, onPendingChange]);

  const handleToggle = (collection: CollectionRecord) => {
    const next = new Map(chosenRecords);
    if (next.has(collection.collection_id)) {
      next.delete(collection.collection_id);
    } else {
      next.set(collection.collection_id, collection);
    }
    setChosenRecords(next);
  };

  const handleSubmit = () => {
    if (chosenRecords.size === 0 || !orgId) {
      return;
    }

    setPartialFailure(null);
    addToCollections(
      {
        collectionIds: Array.from(chosenRecords.keys()),
        resourceOwnerId: orgId,
        resources,
      },
      {
        onSuccess: (result) => {
          const added = result.succeeded.length;
          if (result.failed.length === 0) {
            showNotification(
              `Added to ${added} ${pluralize("collection", added)}`,
            );
            onSuccess?.();
            onCancel();
            return;
          }

          // Some writes failed: say which, keep the dialog open with only
          // the failed collections still chosen, and leave the page's
          // selection alone so the user can retry.
          const failedNames = result.failed
            .map(
              ({ collectionId }) =>
                chosenRecords.get(collectionId)?.name ?? collectionId,
            )
            .join(", ");
          showNotification(
            `Added to ${added} ${pluralize("collection", added)}, but not to ${failedNames}`,
            "warning",
          );
          setPartialFailure(
            <>
              Could not add to {failedNames}:{" "}
              {userFacingErrorMessage(result.failed[0].error)}
            </>,
          );
          setChosenRecords((prev) => {
            const next = new Map(prev);
            result.succeeded.forEach((c) => next.delete(c.collection_id));
            return next;
          });
        },
      },
    );
  };

  const chosenList = React.useMemo(
    () => Array.from(chosenRecords.values()),
    [chosenRecords],
  );

  const addBtnLabel = (() => {
    if (isPending) {
      return "Adding…";
    }
    if (chosenRecords.size === 0) {
      return "Add to collection";
    }
    return `Add to ${chosenRecords.size} ${pluralize("collection", chosenRecords.size)}`;
  })();

  const emptyStateMessage = (() => {
    if (debouncedSearchQuery) {
      return `No collections matching "${debouncedSearchQuery}"`;
    }
    // With the source collection left out, an org whose only collection is
    // that one has none to offer, not none at all.
    if (excludeCollectionIds?.length) {
      return "No other collections yet.";
    }
    return "No collections yet.";
  })();

  const requestError = fetchError ?? mutationError;
  const bannerMessage =
    partialFailure ??
    (requestError ? userFacingErrorMessage(requestError) : null);

  return (
    <>
      {bannerMessage && (
        <div className={styles.errorBanner}>{bannerMessage}</div>
      )}

      <SelectedStrip chosenList={chosenList} onRemove={handleToggle} />

      <div className={styles.searchBar}>
        <Search size={13} className={styles.searchIcon} />
        <input
          className={styles.searchInput}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setIsSearchPending(true);
            debouncedSetSearchQuery(e.target.value);
          }}
          placeholder="Search collections…"
          aria-label="Search collections"
          autoFocus
        />
        {(isSearchPending || isFetching) && searchText && (
          <Loader2 size={13} className={styles.searchSpinner} />
        )}
      </div>

      <div className={styles.list} role="listbox">
        <div
          className={styles.createRow}
          onClick={onCreateNew}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onCreateNew();
            }
          }}
        >
          <div className={styles.createSwatch}>
            <Plus size={14} />
          </div>
          <div className={styles.createLabel}>Create new collection</div>
        </div>

        {collections.length > 0 && (
          <div className={styles.sectionLabel}>
            {sectionLabel(resourceType)}
          </div>
        )}

        {!isFetching && !isSearchPending && collections.length === 0 && (
          <div className={styles.emptyState}>{emptyStateMessage}</div>
        )}

        {collections.map((c) => (
          <CollectionRow
            key={c.collection_id}
            collection={c}
            checked={chosenRecords.has(c.collection_id)}
            onToggle={() => handleToggle(c)}
          />
        ))}
      </div>

      <div className={styles.footer}>
        <RobotoButton
          type="button"
          variant="contained"
          color="secondary"
          disabled={chosenRecords.size === 0 || isPending}
          onClick={handleSubmit}
          eventName="AddToCollectionSubmitClicked"
          eventProperties={{ collectionCount: chosenRecords.size }}
        >
          {addBtnLabel}
        </RobotoButton>
      </div>
    </>
  );
}
